import React, { useState } from 'react';
import { Container, Row, Col, Card, Button, Form, Badge, Image, Alert } from 'react-bootstrap';
import {
  Heart,
  MessageCircle,
  Share2,
  Bookmark,
  ThumbsUp,
  ThumbsDown,
  Send,
  User,
  Calendar,
  Clock,
  Tag,
  Facebook,
  Twitter,
  Linkedin,
  Link as LinkIcon,
  ChevronLeft,
  ChevronRight
} from 'lucide-react';
import small2 from "../../../assets/images/small/img-6.jpg"

const BlogDetail = () => {
  const [liked, setLiked] = useState(false);
  const [bookmarked, setBookmarked] = useState(false);
  const [comment, setComment] = useState('');
  const [showAlert, setShowAlert] = useState(false);
  
  const blogPost = {
    title: "Les meilleures pratiques pour rester en forme",
    author: {
      name: "Marie Laurent",
      role: "Développeuse Senior",
      avatar: "/api/placeholder/100/100",
      followers: 1200,
      following: 890,
      posts: 45
    },
    date: "22 Octobre, 2024",
    readTime: "8 minutes",
    image: small2,
    content: `
      Le développement web moderne a connu une véritable révolution avec l'avènement de React...
      
      React a introduit un nouveau paradigme dans le développement d'interfaces utilisateur avec son approche basée sur les composants...
      
      Les hooks ont représenté un tournant majeur dans la façon dont nous gérons l'état et les effets de bord...
    `,
    tags: ["React", "JavaScript", "Développement Web", "Frontend"],
    likes: 234,
    comments: [
      {
        id: 1,
        author: "Jean Dupont",
        avatar: "/api/placeholder/50/50",
        content: "Excellent article ! J'apprécie particulièrement l'analyse approfondie des hooks.",
        date: "Il y a 2 heures",
        likes: 12,
        dislikes: 1,
        replies: [
          {
            id: 2,
            author: "Sophie Martin",
            avatar: "/api/placeholder/50/50",
            content: "Tout à fait d'accord ! Les hooks ont vraiment simplifié la gestion de l'état.",
            date: "Il y a 1 heure",
            likes: 5,
            dislikes: 0
          }
        ]
      }
    ]
  };

  const relatedPosts = [
    {
      id: 1,
      title: "Les meilleures pratiques React en 2024",
      image: "/api/placeholder/400/200",
      date: "20 Oct, 2024"
    },
    {
      id: 2,
      title: "Introduction à React Server Components",
      image: "/api/placeholder/400/200",
      date: "19 Oct, 2024"
    }
  ];

  const handleComment = () => {
    if (comment.trim()) {
      setShowAlert(true);
      setComment('');
      setTimeout(() => setShowAlert(false), 3000);
    }
  };

  return (
    <Container className="py-1">
      {showAlert && (
        <Alert 
          variant="success" 
          className="position-fixed top-0 start-50 translate-middle-x mt-4 z-3"
          onClose={() => setShowAlert(false)} 
          dismissible
        >
          Votre commentaire a été publié avec succès !
        </Alert>
      )}

      <Row className="g-4">
        {/* Article principal */}
        <Col lg={8}>
          <Card className="border-0 shadow-sm">
            <Card.Body className="p-4">
              {/* En-tête de l'article */}
              <div className="d-flex justify-content-between align-items-center mb-4">
                <div className="d-flex gap-3 align-items-center">
                  <Image 
                    src={blogPost.author.avatar} 
                    roundedCircle 
                    width={50} 
                    height={50} 
                  />
                  <div>
                    <h6 className="mb-0">{blogPost.author.name}</h6>
                    <small className="text-muted">
                      {blogPost.author.role}
                    </small>
                  </div>
                </div>
                <div className="d-flex gap-2">
                  <Button 
                    variant={bookmarked ? "primary" : "outline-primary"} 
                    className="rounded-circle p-2"
                    onClick={() => setBookmarked(!bookmarked)}
                  >
                    <Bookmark size={20} />
                  </Button>
                  <Button 
                    variant="outline-primary" 
                    className="rounded-circle p-2"
                  >
                    <Share2 size={20} />
                  </Button>
                </div>
              </div>

              {/* Image principale */}
              <Image 
                src={blogPost.image} 
                className="w-100 rounded-3 mb-4" 
                style={{ height: '400px', objectFit: 'cover' }} 
              />

              {/* Titre et métadonnées */}
              <h1 className="mb-3">{blogPost.title}</h1>
              <div className="d-flex gap-4 mb-4">
                <span className="d-flex align-items-center gap-2 text-muted">
                  <Calendar size={18} />
                  {blogPost.date}
                </span>
                <span className="d-flex align-items-center gap-2 text-muted">
                  <Clock size={18} />
                  {blogPost.readTime} de lecture
                </span>
              </div>

              {/* Contenu */}
              <div className="mb-4">
                {blogPost.content.split('\n\n').map((paragraph, idx) => (
                  <p key={idx} className="text-muted">{paragraph}</p>
                ))}
              </div>

              {/* Tags */}
              <div className="d-flex gap-2 flex-wrap mb-4">
                {blogPost.tags.map((tag, idx) => (
                  <Badge 
                    key={idx} 
                    bg="light" 
                    text="dark" 
                    className="px-3 py-2 d-flex align-items-center gap-2"
                  >
                    <Tag size={16} />
                    {tag}
                  </Badge>
                ))}
              </div>

              {/* Actions */}
              <div className="d-flex justify-content-between align-items-center border-top border-bottom py-3">
                <div className="d-flex gap-3">
                  <Button 
                    variant={liked ? "primary" : "outline-primary"}
                    className="d-flex align-items-center gap-2"
                    onClick={() => setLiked(!liked)}
                  >
                    <Heart size={20} />
                    {blogPost.likes + (liked ? 1 : 0)}
                  </Button>
                  <Button 
                    variant="outline-primary"
                    className="d-flex align-items-center gap-2"
                  >
                    <MessageCircle size={20} />
                    {blogPost.comments.length}
                  </Button>
                </div>
                <div className="d-flex gap-2">
                  <Button variant="outline-primary" className="rounded-circle p-2">
                    <Facebook size={20} />
                  </Button>
                  <Button variant="outline-primary" className="rounded-circle p-2">
                    <Twitter size={20} />
                  </Button>
                  <Button variant="outline-primary" className="rounded-circle p-2">
                    <Linkedin size={20} />
                  </Button>
                  <Button variant="outline-primary" className="rounded-circle p-2">
                    <LinkIcon size={20} />
                  </Button>
                </div>
              </div>

              {/* Section commentaires */}
              <div className="mt-4">
                <h4 className="mb-4">Commentaires ({blogPost.comments.length})</h4>
                <Form className="mb-4">
                  <Form.Group>
                    <Form.Control
                      as="textarea"
                      rows={3}
                      placeholder="Partagez votre avis..."
                      value={comment}
                      onChange={(e) => setComment(e.target.value)}
                    />
                  </Form.Group>
                  <Button 
                    variant="primary" 
                    className="mt-3 d-flex align-items-center gap-2"
                    onClick={handleComment}
                  >
                    <Send size={18} />
                    Publier
                  </Button>
                </Form>

                {/* Liste des commentaires */}
                {blogPost.comments.map(comment => (
                  <Card key={comment.id} className="border-0 shadow-sm mb-3">
                    <Card.Body>
                      <div className="d-flex gap-3">
                        <Image 
                          src={blogPost.image} 
                          roundedCircle 
                          width={40} 
                          height={40} 
                        />
                        <div className="flex-grow-1">
                          <div className="d-flex justify-content-between align-items-center mb-2">
                            <h6 className="mb-0">{comment.author}</h6>
                            <small className="text-muted">{comment.date}</small>
                          </div>
                          <p className="mb-2">{comment.content}</p>
                          <div className="d-flex gap-3">
                            <Button 
                              variant="link" 
                              className="p-0 text-muted d-flex align-items-center gap-1"
                            >
                              <ThumbsUp size={16} />
                              {comment.likes}
                            </Button>
                            <Button 
                              variant="link" 
                              className="p-0 text-muted d-flex align-items-center gap-1"
                            >
                              <ThumbsDown size={16} />
                              {comment.dislikes}
                            </Button>
                            <Button variant="link" className="p-0 text-muted">
                              Répondre
                            </Button>
                          </div>

                          {/* Réponses aux commentaires */}
                          {comment.replies?.map(reply => (
                            <div key={reply.id} className="border-0 bg-light mt-3 p-3" style={{ borderRadius: '0.25rem' }}>
                              <div className="d-flex gap-3">
                                <Image 
                                  src={blogPost.image} 
                                  roundedCircle 
                                  width={35} 
                                  height={35} 
                                />
                                <div>
                                  <div className="d-flex justify-content-between align-items-center mb-2">
                                    <h6 className="mb-0">{reply.author}</h6>
                                                                       <small className="text-muted">{reply.date}</small>
                                                                   </div>
                                  <p className="mb-2">{reply.content}</p>
                                  <div className="d-flex gap-3">
                                    <Button 
                                      variant="link" 
                                      className="p-0 text-muted d-flex align-items-center gap-1"
                                    >
                                      <ThumbsUp size={16} />
                                      {reply.likes}
                                    </Button>
                                    <Button 
                                      variant="link" 
                                      className="p-0 text-muted d-flex align-items-center gap-1"
                                    >
                                      <ThumbsDown size={16} />
                                      {reply.dislikes}
                                    </Button>
                                  </div>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    </Card.Body>
                  </Card>
                ))}
              </div>
            </Card.Body>
          </Card>
        </Col>

        {/* Sidebar */}
        <Col lg={4}>
          {/* Profil de l'auteur */}
          <Card className="border-0 shadow-sm mb-4">
            <Card.Body className="text-center p-4">
              <Image 
                src={blogPost.author.avatar} 
                roundedCircle 
                width={100} 
                height={100} 
                className="mb-3" 
              />
              <h5>{blogPost.author.name}</h5>
              <p className="text-muted mb-4">{blogPost.author.role}</p>
              
              <Row className="text-center mb-4">
                <Col>
                  <h6 className="mb-0">{blogPost.author.posts}</h6>
                  <small className="text-muted">Articles</small>
                </Col>
                <Col>
                  <h6 className="mb-0">{blogPost.author.followers}</h6>
                  <small className="text-muted">Abonnés</small>
                </Col>
                <Col>
                  <h6 className="mb-0">{blogPost.author.following}</h6>
                  <small className="text-muted">Abonnements</small>
                </Col>
              </Row>
              
              <Button variant="primary" className="w-100">
                Suivre
              </Button>
            </Card.Body>
          </Card>

          {/* Articles similaires */}
        {/*   <Card className="border-0 shadow-sm">
            <Card.Body className="p-4">
              <h5 className="mb-4">Articles similaires</h5>
              {relatedPosts.map(post => (
                <div key={post.id} className="mb-4">
                  <Image 
                    src={post.image} 
                    className="w-100 rounded mb-3" 
                    style={{ height: '150px', objectFit: 'cover' }} 
                  />
                  <h6 className="mb-2">{post.title}</h6>
                  <small className="text-muted">{post.date}</small>
                </div>
              ))}
            </Card.Body>
          </Card> */}
        </Col>
      </Row>

      {/* Navigation entre articles */}
      <div className="d-flex justify-content-between mt-4">
        <Button 
          variant="outline-primary" 
          className="d-flex align-items-center gap-2"
        >
          <ChevronLeft size={20} />
          Article précédent
        </Button>
        <Button 
          variant="outline-primary" 
          className="d-flex align-items-center gap-2"
        >
          Article suivant
          <ChevronRight size={20} />
        </Button>
      </div>
    </Container>
  );
};

export default BlogDetail;